import { useEffect, useMemo, useState } from 'react';
import { Trash2, RefreshCcw, Download, Eye, X } from 'lucide-react';

/**
 * Phase 15.5.2 — Admin-View für Bug-Reports + Auto-Errors.
 *
 * Schutz: Cloudflare Access deckt die `/admin/*`-Route plattformseitig ab.
 * Im Code zusätzlich Hinweis-Banner oben — Defense-in-Depth, falls Access
 * beim Setup vergessen wird.
 */

interface RawLog {
  trackingId?: string;
  type?: string;
  timestamp?: number;
  pseudonym?: string;
  version?: string;
  gitHash?: string;
  expected?: string;
  actual?: string;
  steps?: string | null;
  message?: string;
  reason?: string;
  source?: string;
  recentLogs?: unknown;
  gameStateSnapshot?: unknown;
  userAgent?: string;
  viewport?: string;
  [k: string]: unknown;
}

type TypeFilter = 'all' | 'bug-report' | 'auto-error';

export default function AdminLogs() {
  const [logs, setLogs] = useState<RawLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
  const [pseudonymQuery, setPseudonymQuery] = useState('');
  const [versionQuery, setVersionQuery] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [detail, setDetail] = useState<RawLog | null>(null);

  const fetchLogs = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/admin/logs?limit=500');
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = (await res.json()) as { logs: RawLog[] };
      setLogs(data.logs);
    } catch (e) {
      setError(String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchLogs();
  }, []);

  const filtered = useMemo(() => {
    return logs.filter((l) => {
      if (typeFilter !== 'all' && l.type !== typeFilter) return false;
      if (pseudonymQuery && !(l.pseudonym ?? '').toLowerCase().includes(pseudonymQuery.toLowerCase()))
        return false;
      if (versionQuery && !(l.version ?? '').includes(versionQuery)) return false;
      if (l.timestamp) {
        if (dateFrom) {
          const fromMs = new Date(dateFrom).getTime();
          if (l.timestamp < fromMs) return false;
        }
        if (dateTo) {
          const toMs = new Date(dateTo).getTime() + 24 * 60 * 60_000;
          if (l.timestamp >= toMs) return false;
        }
      }
      return true;
    });
  }, [logs, typeFilter, pseudonymQuery, versionQuery, dateFrom, dateTo]);

  const handleDelete = async (id: string) => {
    if (!window.confirm(`Log ${id} wirklich löschen?`)) return;
    try {
      const res = await fetch(`/api/admin/logs?id=${encodeURIComponent(id)}`, {
        method: 'DELETE',
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setLogs((cur) => cur.filter((l) => l.trackingId !== id));
    } catch (e) {
      window.alert(`Löschen fehlgeschlagen: ${String(e)}`);
    }
  };

  const handleExport = () => {
    const blob = new Blob([JSON.stringify(filtered, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `dispatch24-logs-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <main className="min-h-screen bg-bg-primary px-4 py-8 text-fg-primary md:px-6">
      <div className="mx-auto max-w-7xl">
        {/* Hinweis-Banner */}
        <div className="mb-6 rounded-md border border-amber-500/40 bg-amber-500/10 p-4">
          <p className="text-sm font-semibold text-amber-300">
            ⚠ Admin-Bereich · Nur für Entwickler
          </p>
          <p className="mt-1 text-xs text-fg-muted">
            Diese Route ist via Cloudflare Access geschützt. Wenn du das hier siehst, bist du
            authentifiziert.
          </p>
        </div>

        <header className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="font-display text-3xl font-bold">Logs</h1>
            <p className="text-sm text-fg-muted">
              {loading ? 'Lade …' : `${filtered.length} von ${logs.length} Einträgen`}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={fetchLogs}
              className="inline-flex items-center gap-1.5 rounded-md border border-border-strong bg-bg-elevated px-3 py-2 text-sm font-semibold transition hover:bg-bg-elevated/70"
            >
              <RefreshCcw className="h-3.5 w-3.5" aria-hidden /> Aktualisieren
            </button>
            <button
              type="button"
              onClick={handleExport}
              className="inline-flex items-center gap-1.5 rounded-md bg-accent-dispatch px-3 py-2 text-sm font-semibold text-white transition hover:opacity-90"
            >
              <Download className="h-3.5 w-3.5" aria-hidden /> Export (JSON)
            </button>
          </div>
        </header>

        {/* Filter */}
        <div className="mb-4 grid gap-3 rounded-lg border border-border-subtle bg-bg-secondary p-4 md:grid-cols-5">
          <FilterField label="Typ">
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value as TypeFilter)}
              className="w-full rounded-md border border-border-subtle bg-bg-primary p-2 text-sm"
            >
              <option value="all">Alle</option>
              <option value="bug-report">Bug-Report</option>
              <option value="auto-error">Auto-Error</option>
            </select>
          </FilterField>
          <FilterField label="Pseudonym">
            <input
              type="text"
              value={pseudonymQuery}
              onChange={(e) => setPseudonymQuery(e.target.value)}
              className="w-full rounded-md border border-border-subtle bg-bg-primary p-2 text-sm"
              placeholder="Anonym-…"
            />
          </FilterField>
          <FilterField label="Version">
            <input
              type="text"
              value={versionQuery}
              onChange={(e) => setVersionQuery(e.target.value)}
              className="w-full rounded-md border border-border-subtle bg-bg-primary p-2 text-sm"
              placeholder="0.15"
            />
          </FilterField>
          <FilterField label="Von">
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="w-full rounded-md border border-border-subtle bg-bg-primary p-2 text-sm"
            />
          </FilterField>
          <FilterField label="Bis">
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="w-full rounded-md border border-border-subtle bg-bg-primary p-2 text-sm"
            />
          </FilterField>
        </div>

        {error && (
          <div className="mb-4 rounded-md border border-accent-emergency/40 bg-accent-emergency/10 p-3 text-sm text-accent-emergency">
            Fehler beim Laden: {error}
          </div>
        )}

        {/* Tabelle */}
        <div className="overflow-x-auto rounded-lg border border-border-subtle bg-bg-secondary">
          <table className="w-full text-left text-sm">
            <thead className="bg-bg-primary/40 text-xs uppercase tracking-wider text-fg-muted">
              <tr>
                <th className="px-4 py-3">Zeitpunkt</th>
                <th className="px-4 py-3">Typ</th>
                <th className="px-4 py-3">Pseudonym</th>
                <th className="px-4 py-3">Version</th>
                <th className="px-4 py-3">Beschreibung</th>
                <th className="px-4 py-3 text-right">Aktion</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((l, i) => (
                <tr
                  key={(l.trackingId ?? '') + i}
                  className="border-t border-border-subtle hover:bg-bg-elevated/30"
                >
                  <td className="px-4 py-3 font-mono text-xs">
                    {l.timestamp ? new Date(l.timestamp).toISOString().replace('T', ' ').slice(0, 19) : '—'}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={[
                        'rounded-full px-2 py-0.5 text-[10px] font-semibold',
                        l.type === 'bug-report'
                          ? 'bg-amber-500/20 text-amber-300'
                          : 'bg-accent-emergency/20 text-accent-emergency',
                      ].join(' ')}
                    >
                      {l.type ?? 'unknown'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs">{l.pseudonym ?? '—'}</td>
                  <td className="px-4 py-3 font-mono text-xs">{l.version ?? '—'}</td>
                  <td className="max-w-md truncate px-4 py-3 text-xs">
                    {summaryFor(l)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      type="button"
                      onClick={() => setDetail(l)}
                      aria-label="Details"
                      className="inline-flex h-8 w-8 items-center justify-center rounded-md text-fg-muted transition hover:bg-bg-elevated hover:text-fg-primary"
                    >
                      <Eye className="h-4 w-4" aria-hidden />
                    </button>
                    <button
                      type="button"
                      onClick={() => l.trackingId && void handleDelete(l.trackingId)}
                      aria-label="Löschen"
                      className="inline-flex h-8 w-8 items-center justify-center rounded-md text-fg-muted transition hover:bg-accent-emergency/20 hover:text-accent-emergency"
                    >
                      <Trash2 className="h-4 w-4" aria-hidden />
                    </button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && !loading && (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-sm text-fg-muted">
                    Keine Logs passen zum Filter.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detail-Modal */}
      {detail && <DetailModal log={detail} onClose={() => setDetail(null)} />}
    </main>
  );
}

function summaryFor(l: RawLog): string {
  if (l.type === 'bug-report') return (l.actual ?? l.expected ?? '').slice(0, 100);
  if (l.type === 'auto-error') return String(l.message ?? l.reason ?? l.source ?? '').slice(0, 100);
  return JSON.stringify(l).slice(0, 100);
}

function FilterField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block text-xs">
      <span className="mb-1 block text-fg-muted">{label}</span>
      {children}
    </label>
  );
}

function DetailModal({ log, onClose }: { log: RawLog; onClose: () => void }) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-bg-overlay/90 p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-3xl overflow-hidden rounded-lg border border-border-strong bg-bg-secondary shadow-2xl">
        <header className="flex items-center justify-between border-b border-border-subtle px-5 py-3">
          <p className="font-display text-lg font-semibold">
            Log-Details · {log.trackingId ?? '?'}
          </p>
          <button
            type="button"
            onClick={onClose}
            aria-label="Schließen"
            className="text-fg-muted hover:text-fg-primary"
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        </header>
        <pre className="max-h-[70vh] overflow-auto bg-bg-primary p-4 font-mono text-xs leading-relaxed text-fg-secondary">
          {JSON.stringify(log, null, 2)}
        </pre>
      </div>
    </div>
  );
}
